import { CloseIcon } from "icons/close/CloseIcon";
import CustomPlansIcon from "icons/header/CustomPlansIcon";
import { NotificationIcon } from "icons/header/NotificationIcon";
import InvoiceIcon from "icons/header/InvoiceIcon";
import PaymentAgreementIcon from "icons/header/PaymentAgreementIcon";
import PaymentLinkIcon from "icons/header/PaymentLinkIcon";
import ProcessPaymentIcon from "icons/header/ProcessPaymentIcon";
import ProfileIcon from "icons/header/ProfileIcon";
import QuoteIcon from "icons/header/QuoteIcon";
import SubscriptionIcon from "icons/header/SubscriptionIcon";
import { CoinIcon } from "icons/sidebar/CoinIcon";
import { useEffect, useState, FC } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showPreview } from "store/slices/actionSlice";
import {
  notificationGet,
  useUserData,
  notificationRead,
  updateNotificationCount,
  updateIsReadAllNotification,
} from "store/slices/authSlice";
import { RootState } from "store/store";
import NotificationMessage from "components/notifications/NotificationMessage";
import { NotificationRaw, NotificationReadPayload } from "types/notification";
import { formatRelativeTime } from "utils/DateFormat";
import OffersAvailable from "icons/header/OffersAvailable";

interface NotificationProps {
  setOpenNotifictaion: (open: boolean) => void;
}

type NotificationItem = NotificationRaw;

const Notification: FC<NotificationProps> = ({ setOpenNotifictaion }) => {
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();

  const [isLoadingActivity, setIsLoadingActivity] = useState<boolean>(false);
  const [isMoreActivity, setIsMoreActivity] = useState<boolean>(true);
  const [activityList, setActivityList] = useState<NotificationItem[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [limit] = useState<number>(7);
  // Re-render once a minute so the ages shown here keep pace with the ones on
  // the notifications page instead of drifting apart while the panel is open.
  const [, setNowTick] = useState<number>(0);
  useEffect(() => {
    const timer = setInterval(() => setNowTick((tick) => tick + 1), 60000);
    return () => clearInterval(timer);
  }, []);
  const { notificationCount, isReadAllNotification } = useAppSelector(
    (state: RootState) => state.auth
  );

  let accountData = userData?.data?.accountData;

  const readNotification = async (
    activiy_id: string | number = "",
    is_read = false,
    is_all_read = false
  ) => {
    try {
      let arr = [...activityList];
      if (is_all_read) {
        await dispatch(updateIsReadAllNotification(true));
      } else {
        let updated = arr.map((item) => {
          if (item.id === activiy_id) {
            return { ...item, is_read: 1 };
          } else {
            return { ...item };
          }
        });
        if (notificationCount) {
          await dispatch(updateNotificationCount(notificationCount - 1));
        }
        setActivityList(updated);
      }

      let payload: NotificationReadPayload = {
        accounts_id: accountData.id,
        is_all_read: is_all_read,
        notificartion_read: true,
        is_read: is_read,
      };

      if (!is_all_read) {
        payload["activity_id"] = activiy_id;
      }

      await dispatch(notificationRead(payload)).unwrap();
    } catch (error) {
      console.log("readNotification error =====", error);
    }
  };

  const markAllAsRead = async () => {
    let arr = [...activityList];
    let updated = arr.map((item) => {
      return { ...item, is_read: 1 };
    });
    setActivityList(updated);
    await dispatch(updateIsReadAllNotification(false));
  };

  useEffect(() => {
    if (isReadAllNotification) {
      markAllAsRead();
    }
  }, [isReadAllNotification]);

  const handleFormatList = async (data: NotificationRaw[], isNew?: boolean) => {
    let newList: NotificationRaw[] = [];
    if (isNew) {
      newList = data;
    } else {
      newList.push(...activityList);
      newList.push(...data);
    }
    // The age is worked out while rendering rather than stored here, so it
    // keeps counting up on its own while the panel stays open.
    setActivityList(newList);
    setIsLoadingActivity(false);
  };

  const fetchActivityList = async (isNew?: boolean) => {
    try {
      setIsLoadingActivity(true);
      let payload = {
        accounts_id: userData?.data?.accountData?.id,
        offset: offset,
        limit: limit,
      };
      let res: any = await dispatch(notificationGet(payload)).unwrap();
      const { data, status } = res.body;
      if (status && data?.length > 0) {
        await handleFormatList(data, isNew);
      } else {
        // Nothing came back. The loading flag has to be cleared here too —
        // leaving it on kept the placeholder rows spinning forever for anyone
        // whose notification list is empty.
        setIsMoreActivity(false);
        setIsLoadingActivity(false);
        return;
      }
      setOffset((state) => state + limit);
    } catch (error) {
      setIsLoadingActivity(false);
      console.log("onGetNotification error", error);
    }
  };

  useEffect(() => {
    fetchActivityList(true);
  }, [location]);

  const [loadMoreRef] = useInfiniteScroll({
    loading: isLoadingActivity,
    hasNextPage: isMoreActivity,
    onLoadMore: () => {
      fetchActivityList(false);
    },
    disabled: isLoadingActivity,
    rootMargin: "0px 0px 10px 0px",
  });

  return (
    <div className="p-5 flex flex-col items-center gap-5 flex-1 min-h-0">
      <div className="w-full pb-3 flex justify-between items-center border-b border-strokePrimary shrink-0">
        <div className="text-txtPrimary lg:text-xl text-lg font-medium">
          Notifications
        </div>
        <div className="text-sm flex items-center justify-center gap-5">
          <div
            className="cursor-pointer text-blue hover:!text-btnPrimaryHover  underline "
            onClick={() => readNotification("", false, true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                readNotification("", false, true);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Mark all notifications as read"
          >
            Mark all as read
          </div>
          {/* -mr-2 cancels the padding on the right so the cross lines up with
              the right edge of the rows below it, while the padding still
              gives it a comfortable tap target. */}
          <span
            className="cursor-pointer p-2 -mr-2 rounded-full hover:!bg-bgSecondary"
            onClick={() => setOpenNotifictaion(false)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpenNotifictaion(false);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close notifications"
          >
            <CloseIcon size={20} />
          </span>
        </div>
      </div>
      <div className="-pt-5 w-full flex-1 min-h-0 flex flex-col">
        {/* Both panels now size themselves to the window, so the list simply
            takes the room that is left. */}
        <div className="w-full overflow-x-clip flex-1 min-h-0 max-h-[560px] flex-col flex overflow-y-scroll">
          {isLoadingActivity &&
            activityList?.length <= 0 &&
            [1, 2, 3, 4, 5, 6].map((item: number, index: number) => {
              return (
                <div className="flex items-center gap-2 mb-3" key={index}>
                  <Skeleton circle width={40} height={40} />
                  <div className="flex flex-col gap-1">
                    <Skeleton width={167} height={24} />
                    <Skeleton width={300} height={34} />
                  </div>
                </div>
              );
            })}
          {activityList?.length > 0 &&
            activityList.map(
              (data: NotificationItem, index: number) =>
                (data?.event_type === "payment_agreement" ||
                  data?.event_type === "payment_processed" ||
                  data?.event_type === "payment_link" ||
                  data?.event_type === "payment_scheduled" ||
                  data?.event_type === "manual_payment" ||
                  data?.event_type === "custom_plan" ||
                  data?.event_type === "offer_available" ||
                  data?.event_type === "quote" ||
                  data?.event_type === "invoice" ||
                  data?.event_type === "contact" ||
                  data?.event_type === "payment_method" ||
                  data?.event_type === "subscription") && (
                  <div
                    key={index}
                    className="rounded-[7px] flex lg:gap-2 gap-1.5 py-[10px] justify-between items-center cursor-pointer hover:!bg-bgHover"
                  >
                    <div className="w-[80%] flex lg:gap-3 gap-2 justify-start items-center whitespace-normal break-words">
                      <div className="p-[10px] bg-bgSecondary rounded-full">
                        {data?.event_type === "payment_agreement" ? (
                          <PaymentAgreementIcon />
                        ) : data?.event_type === "payment_link" ? (
                          <PaymentLinkIcon />
                        ) : data?.event_type === "payment_processed" ||
                          data?.event_type === "manual_payment" ? (
                          <ProcessPaymentIcon />
                        ) : data?.event_type === "custom_plan" ? (
                          <CustomPlansIcon />
                        ) : data?.event_type === "payment_scheduled" ? (
                          <CustomPlansIcon />
                        ) : data?.event_type === "quote" ? (
                          <QuoteIcon />
                        ) : data?.event_type === "invoice" ? (
                          <InvoiceIcon />
                        ) : data?.event_type === "contact" ? (
                          <ProfileIcon />
                        ) : data?.event_type === "subscription" ? (
                          <SubscriptionIcon />
                        ) : data?.event_type === "payment_method" ? (
                          <CoinIcon
                            width={20}
                            height={20}
                            strokeColor="var(--color-blue)"
                          />
                        ) : data?.event_type === "offer_available" ? (
                          <OffersAvailable />
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-[5px]">
                        <div className="text-txtSecondary text-sm">
                          {/* One shared component builds the wording; this panel just says how a
                              preview should open. */}
                          <NotificationMessage
                            data={data}
                            onOpenPreview={(type, id) =>
                              dispatch(
                                showPreview({
                                  previewType: type,
                                  previewId: id,
                                })
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-fit text-xs text-txtSecondary flex lg:gap-[6px] gap-1 justify-center items-center whitespace-nowrap shrink-0">
                      {formatRelativeTime(data?.event_date)}
                      {data?.is_read === 0 ? (
                        <div
                          className="w-[6px] h-[6px] bg-blue rounded-full cursor-pointer pr-[2px]"
                          onClick={() => readNotification(data.id, true)}
                        ></div>
                      ) : (
                        <div className="w-[6px] h-[6px]  rounded-full cursor-pointer pr-[2px]"></div>
                      )}
                    </div>
                  </div>
                )
            )}
          {/* Same empty state as the notifications page, so the two never word
              the same situation differently. */}
          {!isLoadingActivity && activityList?.length <= 0 && (
            <div className="text-center w-full flex-1 grid place-items-center py-8 px-4">
              <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
                <NotificationIcon
                  width={80}
                  height={80}
                  strokeColor="var(--color-stroke-active)"
                />
                <div>
                  <p className="text-xl text-txtPrimary font-semibold mb-4">
                    No notifications yet
                  </p>
                  <p className="text-base text-txtParagraph">
                    When there is an update about your payments, plans or
                    profile, it will appear here.
                  </p>
                </div>
              </div>
            </div>
          )}
          {!isLoadingActivity && activityList?.length > 0 && isMoreActivity && (
            <div
              id="checking-div"
              ref={loadMoreRef}
              className="h-[50px] w-full"
            >
              {[1, 2].map((item: number, index: number) => {
                return (
                  <div key={index}>
                    <div className="flex items-center gap-2  py-[10px]">
                      <Skeleton circle width={40} height={40} />
                      <div className="flex flex-col gap-1">
                        <Skeleton width={349} height={24} />
                        <Skeleton width={349} height={34} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {isLoadingActivity &&
            isMoreActivity &&
            [1, 2].map((item: number, index: number) => {
              return (
                <div key={index}>
                  <div className="flex items-center gap-2  py-[10px]">
                    <Skeleton circle width={40} height={40} />
                    <div className="flex flex-col gap-1">
                      <Skeleton width={349} height={24} />
                      <Skeleton width={349} height={34} />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <div
        className="py-[10px] w-full border border-strokePrimary text-center rounded-md text-txtParagraph cursor-pointer hover:!bg-bgHover shrink-0"
        onClick={() => {
          setOpenNotifictaion(false);
          navigate(`/${cluster_id}/notification`);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpenNotifictaion(false);
            navigate(`/${cluster_id}/notification`);
          }
        }}
        role="button"
        tabIndex={0}
      >
        See all notifications
      </div>
    </div>
  );
};

export default Notification;
